/**
 * Date Utilities - Timezone-Safe Date Formatting
 * 
 * These utilities ensure dates are formatted correctly regardless of user's timezone.
 * Avoids the common pitfall of using toISOString() which converts to UTC.
 */

/**
 * Format a Date object as YYYY-MM-DD using local timezone
 * @param {Date} date - The date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get today's date as YYYY-MM-DD string in local timezone
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayLocal = () => {
  return formatDateLocal(new Date());
};

/**
 * Add days to a date string (YYYY-MM-DD) and return new date string
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {number} days - Number of days to add (can be negative)
 * @returns {string} New date string in YYYY-MM-DD format
 */
export const addDaysToDateString = (dateStr, days) => {
  const parts = dateStr.split('-');
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1; // Month is 0-indexed
  const day = parseInt(parts[2]);
  
  const date = new Date(year, month, day);
  date.setDate(date.getDate() + days);
  
  return formatDateLocal(date);
};

/**
 * Parse a date string (YYYY-MM-DD) to a Date object at midnight local time
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {Date} Date object
 */
export const parseDateString = (dateStr) => {
  const parts = dateStr.split('-');
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1;
  const day = parseInt(parts[2]);
  return new Date(year, month, day);
};

/**
 * Check if a date string is today
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is today
 */
export const isToday = (dateStr) => {
  return dateStr === getTodayLocal();
};

/**
 * Check if date string is in the past
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} True if date is before today
 */
export const isPastDate = (dateStr) => {
  const date = parseDateString(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date < today;
};
